import { useState, useCallback, useEffect, useRef } from 'react';
import { Edit3, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { ProseMarkdown } from '@/components/widget/prose-markdown';

export interface ContentDescriptionProposal {
  text: string;
  title?: string;
  onConsumed: () => void;
}

interface ContentDescriptionEditableProps {
  description: string;
  placeholder?: string;
  onSave: (newDescription: string) => void;
  className?: string;
  disabled?: boolean;
  maxLength?: number;
  minRows?: number;
  maxRows?: number;
  renderContent?: (content: string) => React.ReactNode;
  onEditingChange?: (state: { isEditing: boolean; value: string } | null) => void;
  proposal?: ContentDescriptionProposal;
}

export function ContentDescriptionEditable({
  description,
  placeholder = "Click to add description",
  onSave,
  className,
  disabled = false,
  maxLength = 5000,
  minRows = 3,
  maxRows = 10,
  renderContent,
  onEditingChange,
  proposal
}: ContentDescriptionEditableProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(description);
  const [isSaving, setIsSaving] = useState(false);
  const [externalChange, setExternalChange] = useState(false);
  const isEditingRef = useRef(isEditing);
  isEditingRef.current = isEditing;
  const editValueRef = useRef(editValue);
  editValueRef.current = editValue;
  const onEditingChangeRef = useRef(onEditingChange);
  onEditingChangeRef.current = onEditingChange;

  // An unmount mid-edit (route change, or a section remount) must not leave the
  // member's editing state registered anywhere.
  useEffect(
    () => () => {
      if (isEditingRef.current) onEditingChangeRef.current?.(null);
    },
    []
  );

  // While the member is editing, an incoming description (Neo edit, Living Document run,
  // another member) must never replace what they are typing; it only raises a notice.
  useEffect(() => {
    if (isEditingRef.current) {
      setExternalChange(description !== editValueRef.current);
      return;
    }
    setEditValue(description);
  }, [description]);

  useEffect(() => {
    if (proposal === undefined || isEditingRef.current) return;
    setIsEditing(true);
    setEditValue(proposal.text);
    setExternalChange(false);
    onEditingChangeRef.current?.({ isEditing: true, value: proposal.text });
    proposal.onConsumed();
  }, [proposal]);

  const handleEdit = useCallback(() => {
    if (disabled) return;
    setIsEditing(true);
    setEditValue(description);
    setExternalChange(false);
    onEditingChange?.({ isEditing: true, value: description });
  }, [disabled, description, onEditingChange]);

  const handleSave = useCallback(async () => {
    const trimmedValue = editValue.trim();

    // Don't save if value hasn't changed
    if (trimmedValue === description) {
      setIsEditing(false);
      setExternalChange(false);
      onEditingChange?.(null);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(trimmedValue);
      setIsEditing(false);
      setExternalChange(false);
      onEditingChange?.(null);
    } catch (error) {
      console.error('Failed to save description:', error);
      // Keep editing mode open on error
    } finally {
      setIsSaving(false);
    }
  }, [editValue, description, onSave, onEditingChange]);

  const handleCancel = useCallback(() => {
    setEditValue(description);
    setIsEditing(false);
    setExternalChange(false);
    onEditingChange?.(null);
  }, [description, onEditingChange]);

  const handleReloadExternal = useCallback(() => {
    setEditValue(description);
    setExternalChange(false);
    onEditingChange?.({ isEditing: true, value: description });
  }, [description, onEditingChange]);

  const handleApplyProposal = useCallback(() => {
    if (proposal === undefined) return;
    setEditValue(proposal.text);
    onEditingChange?.({ isEditing: true, value: proposal.text });
    proposal.onConsumed();
  }, [proposal, onEditingChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
    // Allow Ctrl+Enter to save
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      handleSave();
    }
  }, [handleSave, handleCancel]);

  // Calculate dynamic height based on content
  const calculateRows = useCallback((text: string) => {
    const lines = text.split('\n').length;
    const estimatedLines = Math.max(lines, Math.ceil(text.length / 80)); // Rough estimate
    return Math.min(Math.max(estimatedLines, minRows), maxRows);
  }, [minRows, maxRows]);

  if (isEditing) {
    return (
      <div className={cn("space-y-3", className)}>
        {externalChange ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground" role="status">
            <span>This section changed while you were editing</span>
            <Button type="button" size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={handleReloadExternal}>
              Reload
            </Button>
            <Button type="button" size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setExternalChange(false)}>
              Keep mine
            </Button>
          </div>
        ) : null}
        {proposal !== undefined ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground" role="status">
            <span>Neo suggested new text</span>
            <Button type="button" size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={handleApplyProposal}>
              Apply
            </Button>
            <Button type="button" size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={proposal.onConsumed}>
              Ignore
            </Button>
          </div>
        ) : null}
        <Textarea
          value={editValue}
          onChange={(e) => {
            setEditValue(e.target.value);
            onEditingChange?.({ isEditing: true, value: e.target.value });
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          maxLength={maxLength}
          rows={calculateRows(editValue)}
          className="resize-none"
          autoFocus
          disabled={isSaving}
        />
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={handleSave}
              disabled={isSaving || editValue.trim() === description}
              className="h-8"
            >
              <Check className="h-4 w-4 mr-1" />
              Save
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleCancel}
              disabled={isSaving}
              className="h-8"
            >
              <X className="h-4 w-4 mr-1" />
              Cancel
            </Button>
          </div>
          <div className="text-xs text-muted-foreground text-right flex flex-col justify-center leading-tight">
            <div>{editValue.length}/{maxLength} characters</div>
            <div>Ctrl+Enter to save</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("group space-y-2", className)}>
      <div
        data-testid="content-description-display"
        className="cursor-pointer rounded-md p-3 border border-transparent hover:border-border hover:bg-muted/50 transition-colors min-h-[60px] relative"
        onClick={handleEdit}
      >
        {description ? (
          renderContent ? (
            renderContent(description)
          ) : (
            <ProseMarkdown text={description} />
          )
        ) : (
          <p className="text-muted-foreground italic text-sm">{placeholder}</p>
        )}
        {!disabled && (
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit();
            }}
            className="absolute top-2 right-2 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <Edit3 className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
}
